import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import React, { useEffect, useMemo, useState } from 'react'
import {
  runCommonSideEffects,
  runPostLoginSideEffects,
} from 'domain/User/actions'
import { useDispatch, useSelector } from 'react-redux'
import {
  userImoNumberSelector,
  userModuleSelector,
  userRoleSelector,
} from 'domain/User/selectors'
import isEqual from 'lodash/isEqual'
import { MODULES } from 'constants/modules'
import paths from 'constants/paths'
import userRoles from 'constants/userRoles'

const isAdminRoute = pathname => pathname.startsWith('/admin')

const isCargoMateRoute = pathname =>
  pathname === paths.overview ||
  pathname === paths.fleet ||
  pathname === paths.fleetStatus ||
  pathname === paths.help ||
  pathname === paths.viewReleaseNotes ||
  pathname === paths.settings ||
  pathname === paths.ssoFailed ||
  pathname === paths.manual ||
  pathname.startsWith(`${paths.manual}/`) ||
  pathname === paths.ports.root ||
  pathname.startsWith(`${paths.ship.root}/`) ||
  isAdminRoute(pathname)

const isRobRoute = pathname =>
  pathname === paths.rob || pathname === paths.robSurvey

const getAuthorizedFallbackPath = ({ imoNumber, userModule, userRole }) => {
  const hasCargoMateModule = userModule?.includes(MODULES.CARGOMATE)
  const hasRobModule = userModule?.includes(MODULES.ROB)

  if (!hasCargoMateModule && !hasRobModule) {
    return paths.noModuleAccess
  }

  if (hasRobModule && !hasCargoMateModule) {
    return paths.rob
  }

  if (hasCargoMateModule && userRole === userRoles.SHIP && imoNumber) {
    return `${paths.ship.root}/${imoNumber}`
  }

  if (hasCargoMateModule) {
    return paths.overview
  }

  if (hasRobModule) {
    return paths.rob
  }

  return paths.noModuleAccess
}

const hasRouteAccess = ({ pathname, userModule, userRole }) => {
  const hasCargoMateModule = userModule?.includes(MODULES.CARGOMATE)
  const hasRobModule = userModule?.includes(MODULES.ROB)

  if (pathname === paths.noModuleAccess) {
    return !hasCargoMateModule && !hasRobModule
  }

  if (isAdminRoute(pathname)) {
    return hasCargoMateModule && userRole === userRoles.ADMIN
  }

  if (isRobRoute(pathname)) {
    return hasRobModule
  }

  if (isCargoMateRoute(pathname)) {
    return hasCargoMateModule
  }

  return true
}

const PrivateRoutes = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const userImoNumber = useSelector(userImoNumberSelector)
  const userModule = useSelector(userModuleSelector)
  const userRole = useSelector(userRoleSelector)

  const [isAuthenticated, setIsAutenticated] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const fallbackPath = useMemo(
    () =>
      getAuthorizedFallbackPath({
        imoNumber: userImoNumber,
        userModule,
        userRole,
      }),
    [userImoNumber, userModule, userRole]
  )

  const isAuthorizedRoute = useMemo(
    () =>
      hasRouteAccess({
        pathname: location.pathname,
        userModule,
        userRole,
      }),
    [location.pathname, userModule, userRole]
  )

  useEffect(() => {
    const savePathAndNavigateToLogin = () => {
      if (!isEqual(location.pathname, paths.overview)) {
        sessionStorage.setItem('go-to-path-on-login', location.pathname)
      }

      navigate(paths.login)
    }

    const authenticate = async () => {
      try {
        await getCurrentUser()

        // Validate that a refreshable session exists — catches expired SSO sessions
        // where getCurrentUser() still resolves from stale localStorage data.
        const session = await fetchAuthSession()

        if (!session?.tokens?.idToken) {
          savePathAndNavigateToLogin()

          return
        }

        // Always run register + feature toggles on every protected route access.
        dispatch(runCommonSideEffects())

        // Only re-fetch user/details on a true browser reload.
        const navigation = performance.getEntriesByType('navigation')[0]

        if (navigation?.type === 'reload') {
          dispatch(runPostLoginSideEffects())
        }

        setIsAutenticated(true)
        setIsLoaded(true)
      } catch (error) {
        savePathAndNavigateToLogin()
      }
    }

    authenticate()
  }, [navigate, location, dispatch])

  return isLoaded ? (
    isAuthenticated ? (
      isAuthorizedRoute ? (
        <Outlet />
      ) : (
        <Navigate replace to={fallbackPath} />
      )
    ) : (
      <Navigate to={paths.login} />
    )
  ) : null
}

export default PrivateRoutes
